/**
 * WaveBlueprintPage — the personalized 10-stage Wave Blueprint.
 *
 * Auto-generated the moment the RFP is sent and the cohort is locked.
 * Pulls /api/brand/waves/{id}/orchestration-plan which already hydrates
 * each of the 10 stages with this wave's actual data (velocity band,
 * unique hook, patient zero, burst plan, role mix, content angles,
 * CTA coverage, post stagger, velocity curve, live cascade rules).
 *
 * Renders all 10 stages stacked vertically as expanded inspector cards
 * — no clicking required, the entire blueprint is visible at a glance.
 *
 * The Wave Control Room links here via the "Wave Blueprint" button.
 * This page never shows live engagement data — it's the locked plan
 * (the recipe). The Adaptive Burst Engine on the Control Room is
 * the live execution layer.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ClipboardList, Activity, Loader2, AlertCircle, ChevronRight, Sparkles } from 'lucide-react';
import DashboardLayout from '../layouts/DashboardLayout';
import { StageStepper, StageInspector } from './WaveControlRoom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WaveBlueprintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [creators, setCreators] = useState([]);
  const [activeStage, setActiveStage] = useState('velocity_band');
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const [planRes, scoresRes] = await Promise.all([
        axios.get(`${API}/api/brand/waves/${id}/orchestration-plan`, { withCredentials: true }),
        axios.get(`${API}/api/brand/waves/${id}/creator-scores`, { withCredentials: true }).catch(() => ({ data: { creators: [] } })),
      ]);
      setPlan(planRes.data);
      setCreators(scoresRes.data?.creators || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load blueprint');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Smooth-scroll the matching inspector card into view when a stepper dot is clicked
  useEffect(() => {
    if (!activeStage) return;
    const el = document.querySelector(`[data-blueprint-stage="${activeStage}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeStage]);

  if (err) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto px-6 py-12 text-center" data-testid="blueprint-error">
          <AlertCircle className="h-8 w-8 text-[#FF4D94] mx-auto mb-3" />
          <p className="text-base font-bold text-white">Couldn't load this wave's blueprint.</p>
          <p className="text-sm text-gray-500 mt-1">{err}</p>
          <button onClick={() => navigate(-1)} className="mt-6 px-4 py-2 rounded-full bg-[#1f1f1f] text-white text-xs font-bold hover:bg-[#2a2a2a]">
            Go back
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (!plan) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto px-6 py-20 text-center text-gray-500" data-testid="blueprint-loading">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          <p className="text-xs uppercase tracking-[0.2em] mt-3">Generating personalized blueprint…</p>
        </div>
      </DashboardLayout>
    );
  }

  const w = plan.wave_meta || {};
  const sentAt = w.sent_at ? new Date(w.sent_at) : null;

  return (
    <DashboardLayout>
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-6" data-testid="wave-blueprint-page">

        {/* Top breadcrumb / actions */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <Link to={`/waves/${id}/control`} data-testid="back-to-control" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-gray-500 hover:text-white transition-colors font-bold">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Control Room
          </Link>
          <Link
            to={`/waves/${id}/record`}
            data-testid="goto-frozen-record"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider hover:brightness-125"
            style={{ background: 'rgba(0,245,255,0.12)', color: '#00F5FF', border: '1px solid rgba(0,245,255,0.35)' }}
          >
            Why these creators?
          </Link>
          <Link
            to={`/waves/${id}/control`}
            data-testid="goto-control-room"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider text-[#0A0A0A] hover:brightness-110"
            style={{ background: '#4ade80' }}
          >
            <Activity className="h-3 w-3" /> Live Engine
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>

        {/* Hero */}
        <header className="mb-8" data-testid="blueprint-hero">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="h-3.5 w-3.5 text-[#FF4D94]" />
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF4D94] font-bold">Wave Blueprint · 10-Stage Plan</p>
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight" data-testid="blueprint-name">{plan.wave_name}</h1>
          <p className="text-[12px] text-gray-400 leading-relaxed mt-2 max-w-3xl">
            <Sparkles className="h-3 w-3 inline align-[-2px] mr-1 text-[#FF4D94]" />
            Auto-generated from your locked cohort, hook & burst plan. This is the recipe — the live execution layer
            (the Adaptive Burst Engine) lives in the Control Room and runs on top of this blueprint every 30 seconds.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Tag label="Wave"     value={plan.wave_name} />
            <Tag label="Creators" value={String(w.creator_count || creators.length || 0)} />
            <Tag label="Platforms" value={(w.platforms || []).filter(Boolean).join(' · ') || '—'} />
            {w.unique_hook && <Tag label="Hook" value={w.unique_hook} mono color="#FF4D94" />}
            {sentAt && <Tag label="Sent" value={sentAt.toLocaleString()} />}
          </div>
        </header>

        {/* Persistent stepper at top — works as a jump-nav */}
        <div className="mb-6 sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur pt-2 pb-2 -mx-5 px-5 lg:-mx-8 lg:px-8 border-b border-[#1f1f1f]">
          <StageStepper plan={plan} active={activeStage} setActive={setActiveStage} waveStatus={w.status} />
        </div>

        {/* Vertical stack of all 10 personalized stage inspectors */}
        <div className="space-y-5" data-testid="blueprint-stages">
          {plan.stages.map((s) => (
            <div key={s.id} data-blueprint-stage={s.id}>
              <StageInspector stage={s} creators={creators} />
            </div>
          ))}
        </div>

        {/* Footer CTA back to live */}
        <div className="mt-12 mb-4 rounded-2xl border border-[#1f1f1f] p-6 text-center"
             style={{ background: 'linear-gradient(135deg, rgba(74,222,128,0.06), rgba(0,212,255,0.04))' }}
             data-testid="blueprint-footer">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#4ade80] font-bold">Blueprint locked · ready to fly</p>
          <h3 className="text-lg font-black text-white mt-2">Watch the Adaptive Burst Engine execute it.</h3>
          <p className="text-[12px] text-gray-400 mt-1.5 max-w-xl mx-auto leading-relaxed">
            Every 30 seconds the engine reads density, drift, cross-engagement and platform signal —
            and mobilizes the roster when reality diverges from this plan.
          </p>
          <Link
            to={`/waves/${id}/control`}
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-wider text-[#0A0A0A] hover:brightness-110 transition-all"
            style={{ background: '#4ade80', boxShadow: '0 0 20px rgba(74,222,128,0.35)' }}
          >
            <Activity className="h-3.5 w-3.5" /> Open Live Engine
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Tag({ label, value, mono, color = '#fff' }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full bg-[#0F0F0F] border border-[#1f1f1f]">
      <span className="text-gray-500">{label}</span>
      <span className={mono ? 'font-mono' : ''} style={{ color }}>{value}</span>
    </span>
  );
}
